// CRM API layer — softphone (Fase 7). Wrappers over crmRequest for the phone
// endpoints of crm/apps/api (modules/phone + presence + preferences + realtime).
//
// Contracts verified against crm/apps/api/src/modules/phone/phone.plugin.ts and
// crm/packages/contracts/src/phone.ts (2026-07-14):
//   - Every /phone/* + /leads/:id/call/* route is requireRole('sales','ops','admin').
//     A role miss is 403 code 'forbidden'; the CONTROL plane (hangup/hold/transfer)
//     additionally enforces ownership → 403 code 'call_not_yours' ("Esta llamada
//     la está gestionando otro asesor"). /presence/* and /preferences/phone only
//     need a session (requireUser).
//   - POST /leads/:id/call/start responds 202 { ok, callId, providerCallId, status }
//     and can fail 404 lead_not_found / 422 lead_without_phone|phone_not_e164 /
//     409 lead_suppressed / 502-503 orchestrator issues.
//   - POST /phone/calls/:id/hangup is FAIL-SOFT server-side: upstream problems
//     come back as 200 { ok:false }, never 5xx (only 403 can reject it).

import { crmRequest } from "./crm";
import { canonicalLeadId } from "./crmLeadId";
import {
  AdvisorAvailability,
  CallState,
  DialBody,
  DialResponse,
  PhoneCallLiveStatus,
  PhonePrefs,
  PhoneResolveResponse,
  PreferenceEnvelope,
  RealtimeTokenResponse,
  StartCallBody,
  StartCallResult,
  TransferCompleteBody,
  TransferConsultResponse,
  WebrtcTokenResponse,
} from "./crmTypes";

// Labels (español) for the live statuses the widget paints. The union is the
// closed set of crm/packages/realtime events.ts phone.call.status. ÚNICA fuente
// de los textos de estado: CallWidget deriva su LIVE_META de aquí.
export const LIVE_STATUS_LABELS: Record<PhoneCallLiveStatus, string> = {
  initiated: "Sonando…",
  answered: "En llamada",
  completed: "Colgada",
};

export const PROVIDER_LABELS: Record<"cloudtalk" | "telnyx", string> = {
  cloudtalk: "Cloudtalk",
  telnyx: "Telnyx",
};

/** Inicia una llamada saliente al lead (click-to-call server-side). 202. */
export function startCall(
  leadUlid: string,
  body: StartCallBody,
): Promise<StartCallResult> {
  return crmRequest<StartCallResult>(
    `/leads/${encodeURIComponent(leadUlid)}/call/start`,
    { method: "POST", body },
  );
}

/** Dialer libre: marca cualquier número (no solo el del lead). El server
 *  normaliza a E.164 (hint de market, default ES) y resuelve el lead por
 *  phone_last9 — si matchea, la llamada pasa por el flujo click-to-call completo.
 *  202. Errores: 422 phone_not_e164 (número no normalizable), 503
 *  phone_orchestrator_not_configured, 502 phone_upstream_error. */
export function dialNumber(body: DialBody): Promise<DialResponse> {
  return crmRequest<DialResponse>("/phone/dial", { method: "POST", body });
}

/** Resuelve un teléfono a un lead (nombre + mercado + leadId hex32) para la
 *  consola flotante. leadId=null si el número no pertenece a ningún lead. */
export function resolveByPhone(phone: string): Promise<PhoneResolveResponse> {
  return crmRequest<PhoneResolveResponse>(
    `/phone/resolve?phone=${encodeURIComponent(phone)}`,
  );
}

/** Cuelga en el orquestador (corta el leg del CLIENTE, no solo el navegador).
 *  Fail-soft server-side: 200 { ok:false } ante problemas upstream. */
export function hangupCall(callId: string): Promise<{ ok: boolean }> {
  return crmRequest<{ ok: boolean }>(
    `/phone/calls/${encodeURIComponent(callId)}/hangup`,
    { method: "POST", body: {} },
  );
}

/** Pausa/reanuda server-side el leg del cliente (hold con música de espera). */
export function holdCall(callId: string, on: boolean): Promise<{ ok: boolean }> {
  return crmRequest<{ ok: boolean }>(
    `/phone/calls/${encodeURIComponent(callId)}/hold`,
    { method: "POST", body: { on } },
  );
}

/** Token de login del softphone WebRTC Telnyx (proxy CRM → orquestador). */
export function webrtcToken(): Promise<WebrtcTokenResponse> {
  return crmRequest<WebrtcTokenResponse>("/phone/webrtc-token", {
    method: "POST",
    body: {},
  });
}

/** Preferencia de proveedor de telefonía del usuario (scope 'phone'). El GET
 *  devuelve { scope, value } con value {} si el usuario nunca guardó nada. */
export function getPhonePrefs(): Promise<PreferenceEnvelope<PhonePrefs>> {
  return crmRequest<PreferenceEnvelope<PhonePrefs>>("/preferences/phone");
}

export function putPhonePrefs(
  value: PhonePrefs,
): Promise<PreferenceEnvelope<PhonePrefs>> {
  return crmRequest<PreferenceEnvelope<PhonePrefs>>("/preferences/phone", {
    method: "PUT",
    body: value,
  });
}

// ── Presencia (requireUser; 204 sin body) ──────────────────────────────────

/** Reporta el estado de llamada del softphone (presencia coordinada). */
export function reportCallState(state: CallState): Promise<void> {
  return crmRequest<void>("/presence/call-state", {
    method: "POST",
    body: { state },
  });
}

/** Refresca last_active_at del usuario (alcanzable para entrantes/transfer). */
export function presenceHeartbeat(): Promise<void> {
  return crmRequest<void>("/presence/heartbeat", { method: "POST", body: {} });
}

/** Asesores con disponibilidad EN VIVO (online + en-llamada) para transferir. */
export function advisorsAvailability(): Promise<{
  items: AdvisorAvailability[];
}> {
  return crmRequest<{ items: AdvisorAvailability[] }>("/advisors/availability");
}

// ── Transferencia atendida ──────────────────────────────────────────────────

/** Paso 1: consulta (holdea al cliente + marca a la asesora destino). */
export function transferConsult(
  callId: string,
  targetAgentEmail: string,
): Promise<TransferConsultResponse> {
  return crmRequest<TransferConsultResponse>(
    `/phone/calls/${encodeURIComponent(callId)}/transfer/consult`,
    { method: "POST", body: { targetAgentEmail } },
  );
}

/** Paso 2a: confirma (el cliente queda con la destino). */
export function transferComplete(
  callId: string,
  body: TransferCompleteBody,
): Promise<{ ok: boolean }> {
  return crmRequest<{ ok: boolean }>(
    `/phone/calls/${encodeURIComponent(callId)}/transfer/complete`,
    { method: "POST", body },
  );
}

/** Paso 2b: cancela (el iniciador vuelve con el cliente). */
export function transferCancel(
  callId: string,
  consultCallControlId?: string,
): Promise<{ ok: boolean }> {
  return crmRequest<{ ok: boolean }>(
    `/phone/calls/${encodeURIComponent(callId)}/transfer/cancel`,
    {
      method: "POST",
      body: consultCallControlId ? { consultCallControlId } : {},
    },
  );
}

// ── Realtime (Mercure) ──────────────────────────────────────────────────────

/**
 * Descubre el hub Mercure y refresca la cookie httpOnly `mercureAuthorization`
 * (el CRM la emite cross-subdominio con MERCURE_COOKIE_DOMAIN, patrón Fase 0).
 * No hace falta env var nueva en este SPA: el hubUrl viene de aquí en runtime.
 */
export function getRealtimeToken(): Promise<RealtimeTokenResponse> {
  return crmRequest<RealtimeTokenResponse>("/realtime/token");
}

/**
 * Namespace LÓGICO de topics Mercure del CRM (packages/realtime/src/topics.ts).
 * Idéntico en stg/prd — no depende del dominio real del hub.
 */
const CRM_TOPIC_BASE = "https://crm.life5.com";

// Los helpers de canonicalización viven ahora en `crmLeadId.ts` (compartidos por
// toda la capa API — links "ver el lead"). Se re-exportan aquí para no romper los
// imports existentes que los tomaban de `crmPhone`.
export { canonicalLeadId, hexUlidToCanonical } from "./crmLeadId";

/** Topic del lead donde apps/jobs publica los `phone.call.status`. El leadUlid
 *  se canonicaliza a Crockford-26 (forma de los publishers) antes de suscribir. */
export function leadTopic(leadUlid: string): string {
  return `${CRM_TOPIC_BASE}/leads/${canonicalLeadId(leadUlid)}`;
}
